import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ images, initialIndex = 0, onClose, portal = true }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const containerRef = useRef(null);

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onClose();
        }
        if (e.key === 'ArrowRight') showNext(e);
        if (e.key === 'ArrowLeft') showPrev(e);
    }, [currentIndex, onClose]);

    const showNext = (e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const showPrev = (e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Touch handlers for swipe
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        setIsDragging(true);
    };

    const onTouchMove = (e) => {
        if (!touchStart) return;
        const currentTouch = e.targetTouches[0].clientX;
        setTouchEnd(currentTouch);
        setDragOffset(currentTouch - touchStart);
    };

    const onTouchEnd = () => {
        setIsDragging(false);
        setDragOffset(0);
        
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        
        if (isLeftSwipe && images.length > 1) {
            showNext();
        }
        if (isRightSwipe && images.length > 1) {
            showPrev();
        }
        
        setTouchStart(null);
        setTouchEnd(null);
    };

    // Handle tap to close (only if not swiping)
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, true);
        // Prevent body scroll when lightbox is open
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = '';
        };
    }, [handleKeyDown]);

    if (!images || images.length === 0) return null;

    const content = (
        <div
            ref={containerRef}
            data-lightbox
            className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center animate-in fade-in duration-300 backdrop-blur-sm touch-none"
            onClick={handleBackdropClick}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            {/* Close button - larger touch target on mobile */}
            <button 
                className="absolute top-4 right-4 text-white/70 hover:text-white active:text-white transition-colors p-4 z-50 touch-manipulation" 
                onClick={(e) => { e.stopPropagation(); onClose(); }}
            >
                <X size={36} />
            </button>
            
            {/* Navigation arrows - hidden on mobile (use swipe instead) */}
            {images.length > 1 && (
                <>
                    <button 
                        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all z-50" 
                        onClick={showPrev}
                    >
                        <ChevronLeft size={48} />
                    </button>
                    <button 
                        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all z-50" 
                        onClick={showNext}
                    >
                        <ChevronRight size={48} />
                    </button>
                </>
            )}
            
            {/* Image container with drag effect */}
            <div 
                className="relative w-full h-full p-4 md:p-12 flex items-center justify-center select-none"
                style={{
                    transform: isDragging ? `translateX(${dragOffset}px)` : 'translateX(0)',
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out'
                }}
            >
                <img 
                    src={images[currentIndex]} 
                    className="max-w-full max-h-full object-contain shadow-2xl rounded-sm pointer-events-none" 
                    alt={`Gallery ${currentIndex}`}
                    draggable="false"
                />
                
                {/* Counter */}
                {images.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-mono backdrop-blur-md">
                        {currentIndex + 1} / {images.length}
                    </div>
                )}
                
                {/* Swipe hint on mobile */}
                {images.length > 1 && (
                    <div className="md:hidden absolute bottom-16 left-1/2 -translate-x-1/2 text-white/50 text-xs">
                        ← Wischen zum Blättern →
                    </div>
                )}
            </div>
        </div>
    );

    return portal ? createPortal(content, document.body) : content;
};
